/**
 * Default cancel flow node parameters.
 *
 * This file is compiled to dist/default-nodes.json during the build process
 * (see createConfigJsonPlugin in build.js). The JSON output is used to
 * provision and update merchant cancel flow node configurations.
 */
import type { CancelFlowNode, CancelFlowNodeType } from '../types';

// Maps each node type to its own `parameters` shape (not the shared union).
// `Extract<CancelFlowNode, { type: K }>` narrows the union to the single variant
// for type K, so `['parameters']` resolves to that variant's parameters type
// rather than a union of all parameters types.
type DefaultNodesMap = { [K in CancelFlowNodeType]: Extract<CancelFlowNode, { type: K }>['parameters'] };

const defaultNodes: DefaultNodesMap = {
  'cancel-flow:start': {
    routing: { next: 'survey' }
  },
  'cancel-flow:survey': {
    cancelReasons: [
      { code: '2' },
      { code: '8' },
      { code: '15' },
      { code: '48' },
      { code: '29' },
      { code: '70' },
      { code: '1' },
      { code: '114' }
    ],
    routing: {
      2: 'skip',
      8: 'retention-discount',
      15: 'sku-swap',
      48: 'retention-discount',
      29: 'retention-discount',
      70: 'pause',
      1: 'retention-discount',
      114: 'benefit-reminder' // used when the user advances without selecting a reason
    },
    locales: {
      'en-US': {
        title: 'Could you tell us why you are canceling?',
        description: 'Your feedback helps us improve your subscription experience.',
        submit: 'Continue',
        cancelReasons: {
          '2': 'I have too much of this product',
          '8': "It's too expensive for me",
          '15': "I didn't like this product",
          '48': "I prefer a different brand's product",
          '29': 'I only wanted the intro offer for subscribing',
          '70': 'I no longer use this product',
          '1': 'It was something else'
        }
      },
      'en-CA': {
        title: 'Could you tell us why you are canceling?',
        description: 'Your feedback helps us improve your subscription experience.',
        submit: 'Continue',
        cancelReasons: {
          '2': 'I have too much of this product',
          '8': "It's too expensive for me",
          '15': "I didn't like this product",
          '48': "I prefer a different brand's product",
          '29': 'I only wanted the intro offer for subscribing',
          '70': 'I no longer use this product',
          '1': 'It was something else'
        }
      },
      'fr-CA': {
        title: 'Pouvez-vous nous dire pourquoi vous annulez?',
        description: "Vos commentaires nous aident à améliorer votre expérience d'abonnement.",
        submit: 'Continuer',
        cancelReasons: {
          '2': "J'ai trop de ce produit",
          '8': "C'est trop cher pour moi",
          '15': "Je n'ai pas aimé ce produit",
          '48': "Je préfère le produit d'une autre marque",
          '29': "Je voulais seulement l'offre d'introduction pour l'abonnement",
          '70': "Je n'utilise plus ce produit",
          '1': "C'était autre chose"
        }
      },
      'es-ES': {
        title: '¿Podría decirnos por qué cancela?',
        description: 'Tus comentarios nos ayudan a mejorar tu experiencia de suscripción.',
        submit: 'Continuar',
        cancelReasons: {
          '2': 'Tengo demasiado de este producto',
          '8': 'Es demasiado caro para mí',
          '15': 'No me gustó este producto',
          '48': 'Prefiero el producto de otra marca',
          '29': 'Solo quería la oferta introductoria por suscribirme',
          '70': 'Ya no uso este producto',
          '1': 'Fue otra cosa'
        }
      }
    }
  },
  'cancel-flow:skip': {
    routing: {
      accepted: null,
      next: 'cancel-subscription'
    },
    locales: {
      'en-US': {
        title: 'Need to skip this delivery?',
        description: 'You can skip this order of {productName} and keep the rest of your subscription on track.',
        acceptButton: 'Skip Product',
        newOrderDate: 'New Order Date'
      },
      'en-CA': {
        title: 'Need to skip this delivery?',
        description: 'You can skip this order of {productName} and keep the rest of your subscription on track.',
        acceptButton: 'Skip Product',
        newOrderDate: 'New Order Date'
      },
      'fr-CA': {
        title: 'Vous avez besoin de sauter cette livraison?',
        description:
          'Vous pouvez sauter cette commande de {productName} et maintenir le reste de votre abonnement sur la bonne voie.',
        acceptButton: 'Ignorer le produit',
        newOrderDate: 'Nouvelle date de commande'
      },
      'es-ES': {
        title: '¿Necesitas omitir esta entrega?',
        description: 'Puedes omitir este pedido de {productName} y mantener el resto de tu suscripción en curso.',
        acceptButton: 'Omitir Producto',
        newOrderDate: 'Nueva Fecha de Pedido'
      }
    }
  },
  'cancel-flow:sku-swap': {
    routing: {
      accepted: null,
      next: 'cancel-subscription'
    },
    locales: {
      'en-US': {
        title: 'Would you like to try a different product instead?',
        subheader: "If this item isn't the right fit, you can swap it for something new.",
        prepaidNote: 'Note: For prepaid subscriptions, you can only swap to products of equal or lesser value.',
        searchPlaceholder: 'Search products',
        searchLabel: 'Search for products',
        clearSearch: 'Clear search',
        noResults: 'No products match your search',
        swapButton: 'Swap Product'
      },
      'en-CA': {
        title: 'Would you like to try a different product instead?',
        subheader: "If this item isn't the right fit, you can swap it for something new.",
        prepaidNote: 'Note: For prepaid subscriptions, you can only swap to products of equal or lesser value.',
        searchPlaceholder: 'Search products',
        searchLabel: 'Search for products',
        clearSearch: 'Clear search',
        noResults: 'No products match your search',
        swapButton: 'Swap Product'
      },
      'fr-CA': {
        title: 'Souhaitez-vous essayer un produit différent à la place?',
        subheader: "Si cet article ne vous convient pas, vous pouvez l'échanger contre quelque chose de nouveau.",
        prepaidNote:
          'Remarque : Pour les abonnements prépayés, vous ne pouvez échanger que vers des produits de valeur égale ou inférieure.',
        searchPlaceholder: 'Rechercher des produits',
        searchLabel: 'Rechercher des produits',
        clearSearch: 'Effacer la recherche',
        noResults: 'Aucun produit ne correspond à votre recherche',
        swapButton: 'Échanger le produit'
      },
      'es-ES': {
        title: '¿Te gustaría probar un producto diferente?',
        subheader: 'Si este artículo no es el adecuado, puedes cambiarlo por algo nuevo.',
        prepaidNote: 'Nota: Para suscripciones prepagadas, solo puedes cambiar a productos de igual o menor valor.',
        searchPlaceholder: 'Buscar productos',
        searchLabel: 'Buscar productos',
        clearSearch: 'Limpiar búsqueda',
        noResults: 'Ningún producto coincide con tu búsqueda',
        swapButton: 'Cambiar Producto'
      }
    }
  },
  'cancel-flow:pause': {
    routing: {
      accepted: null,
      next: 'cancel-subscription'
    },
    locales: {
      'en-US': {
        title: 'Need a break?',
        description: 'You can pause your subscription for free and have it resume at a later date.',
        pauseButton: 'Pause subscription'
      },
      'en-CA': {
        title: 'Need a break?',
        description: 'You can pause your subscription for free and have it resume at a later date.',
        pauseButton: 'Pause subscription'
      },
      'fr-CA': {
        title: "Besoin d'une pause?",
        description: 'Vous pouvez mettre votre abonnement en pause gratuitement et le reprendre à une date ultérieure.',
        pauseButton: "Mettre en pause l'abonnement"
      },
      'es-ES': {
        title: '¿Necesitas un descanso?',
        description: 'Puedes pausar tu suscripción de forma gratuita y reanudarla en una fecha posterior.',
        pauseButton: 'Pausar la suscripción'
      }
    }
  },
  'cancel-flow:retention-discount': {
    routing: {
      accepted: null,
      next: 'cancel-subscription'
    },
    locales: {
      'en-US': {
        title: 'Are you sure you want to cancel?',
        description: "Here's an exclusive offer for staying subscribed.",
        discountHeading: 'Get an extra {discount} off your subscription',
        discountSubheading: 'for your next order',
        acceptButton: 'Get Discount'
      },
      'en-CA': {
        title: 'Are you sure you want to cancel?',
        description: "Here's an exclusive offer for staying subscribed.",
        discountHeading: 'Get an extra {discount} off your subscription',
        discountSubheading: 'for your next order',
        acceptButton: 'Get Discount'
      },
      'fr-CA': {
        title: 'Êtes-vous sûr de vouloir annuler?',
        description: 'Voici une offre exclusive pour rester abonné.',
        discountHeading: 'Obtenez {discount} de réduction supplémentaire sur votre abonnement',
        discountSubheading: 'pour votre prochaine commande',
        acceptButton: 'Obtenir la réduction'
      },
      'es-ES': {
        title: '¿Estás seguro de que quieres cancelar?',
        description: 'Aquí tienes una oferta exclusiva por seguir suscrito.',
        discountHeading: 'Obtén un {discount} extra de descuento en tu suscripción',
        discountSubheading: 'para tu próximo pedido',
        acceptButton: 'Obtener Descuento'
      }
    }
  },
  'cancel-flow:benefit-reminder': {
    routing: {
      accepted: null,
      next: 'cancel-subscription'
    },
    locales: {
      'en-US': {
        title: 'Are you sure you want to cancel?',
        description: "Canceling now means you'll lose your current benefits.",
        benefitHeading: "You're currently getting {discount}% off, along with these subscriber benefits:",
        benefitsList: ['Skip, Pause or Cancel Anytime'],
        acceptButton: 'Keep Benefits'
      },
      'en-CA': {
        title: 'Are you sure you want to cancel?',
        description: "Canceling now means you'll lose your current benefits.",
        benefitHeading: "You're currently getting {discount}% off, along with these subscriber benefits:",
        benefitsList: ['Skip, Pause or Cancel Anytime'],
        acceptButton: 'Keep Benefits'
      },
      'fr-CA': {
        title: 'Êtes-vous sûr de vouloir annuler?',
        description: 'Annuler maintenant signifie que vous perdrez vos avantages actuels.',
        benefitHeading:
          "Vous bénéficiez actuellement de {discount}% de réduction, ainsi que de ces avantages d'abonné :",
        benefitsList: ['Ignorer, mettre en pause ou annuler à tout moment'],
        acceptButton: 'Conserver les avantages'
      },
      'es-ES': {
        title: '¿Estás seguro de que quieres cancelar?',
        description: 'Cancelar ahora significa que perderás tus beneficios actuales.',
        benefitHeading: 'Actualmente obtienes {discount}% de descuento, junto con estos beneficios de suscriptor:',
        benefitsList: ['Omitir, Pausar o Cancelar en Cualquier Momento'],
        acceptButton: 'Conservar Beneficios'
      }
    }
  },
  // Optional fields are included as empty strings because
  // TacticEditor in RC3 discovers available fields from these default locale keys
  'cancel-flow:custom-content': {
    routing: {
      accepted: null,
      next: 'cancel-subscription'
    },
    locales: {
      'en-US': {
        title: 'Wait! Before you go...',
        subheader: 'We have something special to share with you.',
        imageUrl: '',
        imageAltText: '',
        videoUrl: '',
        videoTitle: '',
        bodyText: '',
        buttonLink: '',
        acceptButton: 'Keep Subscription',
        declineButton: 'No thanks, cancel'
      },
      'en-CA': {
        title: 'Wait! Before you go...',
        subheader: 'We have something special to share with you.',
        imageUrl: '',
        imageAltText: '',
        videoUrl: '',
        videoTitle: '',
        bodyText: '',
        buttonLink: '',
        acceptButton: 'Keep Subscription',
        declineButton: 'No thanks, cancel'
      },
      'fr-CA': {
        title: 'Attendez! Avant de partir...',
        subheader: 'Nous avons quelque chose de spécial à partager avec vous.',
        imageUrl: '',
        imageAltText: '',
        videoUrl: '',
        videoTitle: '',
        bodyText: '',
        buttonLink: '',
        acceptButton: "Garder l'abonnement",
        declineButton: 'Non merci, annuler'
      },
      'es-ES': {
        title: 'Espera! Antes de irte...',
        subheader: 'Tenemos algo especial para compartir contigo.',
        imageUrl: '',
        imageAltText: '',
        videoUrl: '',
        videoTitle: '',
        bodyText: '',
        buttonLink: '',
        acceptButton: 'Mantener Suscripción',
        declineButton: 'No gracias, cancelar'
      }
    }
  },
  'cancel-flow:cancel-subscription': {
    routing: { next: null }
  }
};

export default defaultNodes;
